import type {
	Components,
} from '@mui/material'

export const materialUIButtonTheme: {
	components: Components
} = {
	components: {
		MuiButton: {
			defaultProps: {
				variant:          'primary',
				style:            {
					textTransform: 'none',
				},
			},
			styleOverrides: {
				root: {
					lineHeight: 1,
					transition: 'all 0.2s ease 0s',

					/* disabled */
					'&.Mui-disabled': {
						opacity: 0.55,
					},

					'&.MuiButton-loading': {
						color: 'transparent !important',
					},
				},
				sizeSmall: {
					height:       '2rem',
					padding:      'var(--base-spacing-2)',
					fontSize:     'var(--font-size-body-xs-small)',
					borderRadius: 'var(--border-radius-xl)',
				},
				sizeMedium: {
					height:       '2.5rem',
					padding:      'var(--base-spacing-3) var(--base-spacing-3)',
					fontSize:     'var(--font-size-body-x-small)',
					borderRadius: 'var(--border-radius-s)',
				},
				sizeLarge: {
					height:       '3rem',
					padding:      'var(--base-spacing-3) var(--base-spacing-5)',
					fontSize:     'var(--font-size-body-small)',
					borderRadius: 'var(--border-radius-m)',
				},
				startIcon: {
					marginRight: '4px',
				},
			},
			variants: [

				/* variant:primary | base */
				{
					props: {
						variant: 'primary',
					},
					style: {
						color:           'var(--on-primary-color)',
						backgroundColor: 'var(--primary-color)',
						fontWeight:      'var(--font-weight-medium)',

						'&:hover': {
							backgroundColor: 'var(--primary-color-hover)',
						},

						'& .MuiCircularProgress-circle': {
							stroke: 'var(--on-primary-color)',
						},
						'.MuiTouchRipple-root .MuiTouchRipple-child': {
							backgroundColor: 'var(--on-primary-color)',
						},

						'&.Mui-disabled': {
							color: 'var(--on-primary-color)',
						},
					},
				},

				/* variant:primary | base */
				{
					props: {
						variant: 'primary',
						color:   'secondary',
					},
					style: {
						color:           'var(--on-secondary-color)',
						backgroundColor: 'var(--secondary-color)',
						fontWeight:      'var(--font-weight-medium)',

						'&:hover': {
							backgroundColor: 'var(--secondary-color-hover)',
						},

						'& .MuiCircularProgress-circle': {
							stroke: 'var(--on-secondary-color)',
						},
						'.MuiTouchRipple-root .MuiTouchRipple-child': {
							backgroundColor: 'var(--on-secondary-color)',
						},

						'&.Mui-disabled': {
							color: 'var(--on-secondary-color)',
						},
					},
				},

				/* variant:primary | colors */
				/* variant:primary | color:error */
				{
					props: {
						variant: 'primary', color: 'error',
					},
					style: {
						color:           'var(--on-error-color)',
						backgroundColor: 'var(--error-color)',

						'&:hover': {
							backgroundColor: 'var(--error-color-hover)',
						},

						'& .MuiCircularProgress-circle': {
							stroke: 'var(--on-primary-color)',
						},

						'.MuiTouchRipple-root .MuiTouchRipple-child': {
							backgroundColor: 'var(--on-error-color)',
						},
					},
				},

				/* variant:primary | color:success */
				{
					props: {
						variant: 'primary', color: 'success',
					},
					style: {
						color:           'var(--on-positive-color)',
						backgroundColor: 'var(--positive-color)',

						'&:hover': {
							backgroundColor: 'var(--positive-color-hover)',
						},

						'& .MuiCircularProgress-circle': {
							stroke: 'var(--on-positive-color)',
						},

						'.MuiTouchRipple-root .MuiTouchRipple-child': {
							backgroundColor: 'var(--on-positive-color)',
						},
					},
				},

				/* variant:primary | color:warning */
				{
					props: {
						variant: 'primary', color: 'warning',
					},
					style: {
						color:           'var(--on-warning-color)',
						backgroundColor: 'var(--warning-color)',

						'&:hover': {
							backgroundColor: 'var(--warning-color-hover)',
						},

						'& .MuiCircularProgress-circle': {
							stroke: 'var(--on-warning-color)',
						},

						'.MuiTouchRipple-root .MuiTouchRipple-child': {
							backgroundColor: 'var(--on-warning-color)',
						},
					},
				},

				/* variant:outlined | base */
				{
					props: {
						variant: 'outlined',
					},
					style: {
						color:           'var(--gray-color-700)',
						borderColor:  	 'var(--gray-color-200)',
						fontWeight:      'var(--font-weight-medium)',

						'&:hover': {
							borderColor:  	 'var(--gray-color-400)',
						},

						'& .MuiCircularProgress-circle': {
							stroke: 'var(--on-surface-color)',
						},
					},
				},

				/* variant:outlined | colors */
				/* variant:outlined | colors:primary */
				{
					props: {
						variant: 'outlined',
						color:   'primary',
					},
					style: {
						color:           'var(--primary-color)',
						borderColor:  	 'var(--primary-color)',
						fontWeight:      'var(--font-weight-medium)',

						'&:hover': {
							color:           'var(--on-primary-color)',
							backgroundColor: 'var(--primary-color)',
							borderColor:  	  'var(--primary-color-hover)',
						},

						'& .MuiCircularProgress-circle': {
							stroke: 'var(--on-surface-color)',
						},
					},
				},

				{
					props: {
						variant: 'outlined',
						color:   'neutral',
					},
					style: {
						color:           'var(--neutral-color)',
						borderColor:  	 'var(--neutral-color)',
						fontWeight:      'var(--font-weight-medium)',

						'&:hover': {
							color:           'var(--neutral-color-hover)',
							borderColor:  	  'var(--neutral-color-hover)',
						},

						'& .MuiCircularProgress-circle': {
							stroke: 'var(--neutral-color-hover)',
						},
					},
				},

				/* variant:outlined | colors:error */
				{
					props: {
						variant: 'outlined',
						color:   'error',
					},
					style: {
						color:           'var(--error-color)',
						borderColor:  	 'var(--error-color)',
						fontWeight:      'var(--font-weight-medium)',

						'&:hover': {
							color:           'var(--on-error-color)',
							backgroundColor: 'var(--error-color)',
							borderColor:  	  'var(--error-color-hover)',
						},

						'& .MuiCircularProgress-circle': {
							stroke: 'var(--on-surface-color)',
						},
					},
				},

				/* variant:outlined | colors:success */
				{
					props: {
						variant: 'outlined',
						color:   'success',
					},
					style: {
						color:           'var(--positive-color)',
						borderColor:  	 'var(--positive-color)',
						fontWeight:      'var(--font-weight-medium)',

						'&:hover': {
							color:           'var(--on-positive-color)',
							backgroundColor: 'var(--positive-color)',
							borderColor:  	  'var(--positive-color-hover)',
						},

						'& .MuiCircularProgress-circle': {
							stroke: 'var(--on-surface-color)',
						},
					},
				},

				/* variant:outlined | colors:warning */
				{
					props: {
						variant: 'outlined',
						color:   'warning',
					},
					style: {
						color:           'var(--warning-color)',
						borderColor:  	 'var(--warning-color)',
						borderRadius:    'var(--border-radius-xs)',
						fontWeight:      'var(--font-weight-medium)',

						'&:hover': {
							color:           'var(--on-warning-color)',
							backgroundColor: 'var(--warning-color)',
							borderColor:  	  'var(--warning-color-hover)',
						},

						'& .MuiCircularProgress-circle': {
							stroke: 'var(--on-surface-color)',
						},
					},
				},
			],
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					transition: 'background-color, border-color, 0.2s ease 0s',

					':hover': {
					},

					'& .MuiTouchRipple-root .MuiTouchRipple-child': {
						color:        'var(--on-surface-color)',
					},
				},
			},
			variants: [
				{
					props: {
						color: 'primary',
					},
					style: {
						color:           'var(--on-primary-color)',
						backgroundColor: 'var(--primary-color)',
					},
				},
				{
					props: {
						color: 'secondary',
					},
					style: {
						color:           'var(--on-secondary-color)',
						backgroundColor: 'var(--secondary-color)',

						':hover': {
							backgroundColor: 'var(--secondary-color)',
						},
					},
				},
				{
					props: {
						color: 'error',
					},
					style: {
						color:           'var(--error-color)',
						backgroundColor: 'var(--error-color)',

						':hover': {
							backgroundColor: 'var(--error-color)',
						},
					},
				},
				{
					props: {
						color: 'success',
					},
					style: {
						color:           'var(--positive-color)',
						backgroundColor: 'var(--positive-color)',

						':hover': {
							backgroundColor: 'var(--positive-color)',
						},
					},
				},
				{
					props: {
						color: 'warning',
					},
					style: {
						color:           'var(--warning-color)',
						backgroundColor: 'var(--warning-color)',

						':hover': {
							backgroundColor: 'var(--warning-color)',
						},
					},
				},
			],
		},
	},
}
